/**
 * Klaviyo → Shopify Web Pixel Bridge (Lightweight)
 * Captures Klaviyo form events in top window, forwards via postMessage
 * to sandbox iframe(s) where GTM listener pushes to dataLayer.
 * DOM-only detection.  No PII captured.
 * v2.13.0-light
 */
(function KlaviyoBridge() {
  'use strict';

  // === CONFIG ===============================================================
  var CONFIG = {
    // CSS selector for target iframe(s) that contain the GTM listener.
    // Default 'iframe' sends to every iframe — safe because the listener
    // validates origin + schema.  Narrow down if needed, e.g.:
    //   'iframe[src*="web-pixel"]'   — Shopify Web Pixel iframes only
    //   '#my-gtm-frame'              — a specific iframe by ID
    iframeSel: 'iframe',

    sourceName:   'klaviyo_bridge',
    version:      1,
    debug:        false,
    dedupMs:      2000,

    // Klaviyo DOM selectors (adjust per client if markup changes)
    sel: {
      form: [
        '.klaviyo-form',
        '[data-testid="klaviyo-form-component"]',
        'div[role="dialog"][class*="kl-private-reset"]',
        'klaviyo-subscription-form',
        'back-in-stock'
      ].join(', '),
      email:  'input[type="email"], input[name*="email"], input[aria-label*="email" i]',
      sms:    'input[type="tel"], input[name*="phone"], input[name*="sms"], input[aria-label*="phone" i]',
      submit: [
        'button[type="submit"]',
        'button.klaviyo-btn',
        'button[data-testid*="submit"]',
        'button[data-submit]',
        'button[data-send]',
        'fieldset button',
        // Klaviyo popup "Sign Up" button uses type="button" with kl-private class.
        // Exclude close/dismiss buttons and dropdown-opener buttons (aria-haspopup /
        // aria-expanded are set by Klaviyo on country-code selectors and similar pickers).
        'button[class*="kl-private"]:not([class*="close"]):not([aria-label*="lose" i]):not([aria-haspopup]):not([aria-expanded])'
      ].join(', ')
    }
  };

  // === HELPERS ==============================================================
  var EVENTS = [
    'email_box_fired','email_box_started','email_box_submitted',
    'sms_box_fired','sms_box_started','sms_box_submitted'
  ];
  var _dedup = {};

  function uid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function log() {
    if (!CONFIG.debug) return;
    console.log.apply(console, ['[KlaviyoBridge]'].concat([].slice.call(arguments)));
  }

  function isDup(key) {
    var now = Date.now();
    if (_dedup[key] && now - _dedup[key] < CONFIG.dedupMs) return true;
    _dedup[key] = now;
    return false;
  }

  /**
   * Check if a DOM element is truly visible (not hidden via display:none on
   * self or any ancestor).
   *
   * WHY offsetWidth/offsetHeight and not just getComputedStyle(el).display:
   *   CSS `display` is NOT inherited. An input inside a `display:none` parent
   *   reports its OWN display ('inline') via getComputedStyle, not 'none'.
   *   offsetWidth/offsetHeight are 0 for any element in a display:none subtree,
   *   making them the reliable cross-browser check for effective visibility.
   */
  function isVisible(el) {
    if (!el) return false;
    // Fast path: non-fixed rendered elements always have non-null offsetParent.
    if (el.offsetParent !== null) return true;
    // offsetParent is null for: display:none (self or ancestor), position:fixed,
    // or detached nodes.  Use rendered dimensions to distinguish hidden from fixed.
    if (el.offsetWidth > 0 || el.offsetHeight > 0) return true;
    // Last resort: position:fixed element with zero CSS dimensions (rare).
    try {
      var s = getComputedStyle(el);
      return s.position === 'fixed' && s.display !== 'none' && s.visibility !== 'hidden';
    } catch (e) { return false; }
  }

  // === SEND =================================================================
  function send(eventName, p) {
    if (EVENTS.indexOf(eventName) === -1) return;
    var key = eventName + '::' + (p.form_id || '?');
    if (isDup(key)) { log('dedup', key); return; }

    var msg = {
      source:     CONFIG.sourceName,
      version:    CONFIG.version,
      event_name: eventName,
      event_id:   uid(),
      ts:         Date.now(),
      params: {
        form_type:     p.form_type || 'email',
        form_id:       p.form_id   || 'unknown',
        form_name:     p.form_name || '',
        placement:     p.placement || 'popup',
        page_location: location.href,
        page_path:     location.pathname,
        page_title:    document.title
      }
    };
    log('→', eventName, msg);

    // Broadcast to matching iframe(s).
    // targetOrigin must be '*' because Shopify sandbox iframes have
    // origin "null" (sandbox="allow-scripts allow-forms" without
    // allow-same-origin).  Security is enforced on the receiver side
    // via origin allowlist + schema + param whitelist.
    var targets = document.querySelectorAll(CONFIG.iframeSel);
    var sent = 0;
    for (var i = 0; i < targets.length; i++) {
      try { targets[i].contentWindow.postMessage(msg, '*'); sent++; }
      catch (e) { /* cross-origin or detached — skip */ }
    }
    log('delivered to', sent, 'iframe(s)');
  }

  // === FORM HELPERS =========================================================

  /**
   * Extract a stable form ID. Priority:
   *  data-klaviyo-form-id [explicit dev attr] →
   *  data-id → data-form-id → data-sms-id → id →
   *  data-testid "klaviyo-form-XXX" → random fallback (cached on element).
   *
   * IMPORTANT: random fallback is cached in el.__klFormId so that
   * repeated calls from MutationObserver callbacks always return the
   * same ID, keeping the dedup key stable and preventing duplicate events.
   */
  function formId(el) {
    // Developer-supplied explicit ID — always takes priority, no caching needed.
    var explicit = el.getAttribute('data-klaviyo-form-id');
    if (explicit) return explicit;
    // Cached value (stabilises random fallback across MutationObserver calls).
    if (el.__klFormId) return el.__klFormId;
    var id = el.getAttribute('data-id')
          || el.getAttribute('data-form-id')
          || el.getAttribute('data-sms-id')
          || el.id;
    if (id) { el.__klFormId = id; return id; }
    // Extract Klaviyo form ID from data-testid="klaviyo-form-QSY7dD"
    // Note: testid="klaviyo-form-component" yields "component" — not unique if
    // multiple embedded widgets exist on a page; acceptable for single-widget sites.
    var tid = el.getAttribute('data-testid') || '';
    var m = tid.match(/klaviyo-form-(\w+)/);
    if (m) { el.__klFormId = m[1]; return m[1]; }
    var fallback = 'kl_' + Math.random().toString(36).substr(2, 6);
    el.__klFormId = fallback;
    return fallback;
  }

  /**
   * Get a human-readable form name. Priority:
   *  data-klaviyo-form-name [explicit dev attr] →
   *  data-form-name → aria-label → parent dialog aria-label → ""
   */
  function formName(el) {
    var n = el.getAttribute('data-klaviyo-form-name')
         || el.getAttribute('data-form-name')
         || el.getAttribute('aria-label');
    if (n) return n;
    // Inherit from parent dialog (Klaviyo flyout / popup)
    var dlg = el.closest('[role="dialog"]');
    if (dlg) return dlg.getAttribute('aria-label') || '';
    return '';
  }

  /**
   * Determine form placement. Priority:
   *  data-klaviyo-placement [explicit dev attr, "popup"|"embed"] →
   *  CSS heuristics (role=dialog, position:fixed, high z-index).
   */
  function formPlacement(el) {
    var override = el.getAttribute('data-klaviyo-placement');
    if (override === 'popup' || override === 'embed') return override;
    try {
      if (el.matches && el.matches('[role="dialog"]')) return 'popup';
      if (el.closest('[role="dialog"]')) return 'popup';
      var s = getComputedStyle(el);
      if (s.position === 'fixed' || s.position === 'absolute') return 'popup';
      if (el.closest('.klaviyo-form-overlay, [data-testid="klaviyo-form-popup"]')) return 'popup';
      // Theme back-in-stock: wrapper has height:0 but inner popup gets .active class when open.
      // Any element with an active inner popup child is a popup placement.
      if (el.querySelector('[class*="popup"].active, [data-popup].active')) return 'popup';
      var ps = el.parentElement ? getComputedStyle(el.parentElement) : null;
      if (ps && (ps.position === 'fixed' || parseInt(ps.zIndex, 10) > 999)) return 'popup';
    } catch (e) { /* safe fallback */ }
    return 'embed';
  }

  /**
   * Determine current form type. Priority:
   *  data-klaviyo-form-type [explicit dev attr, "email"|"sms"] →
   *  visibility detection (which input is visible).
   *
   * NOTE: Do NOT set data-klaviyo-form-type on multi-step forms (email → sms step).
   * For those, omit the attribute and let the bridge auto-detect the active step
   * via input visibility — the MutationObserver will re-evaluate on each step change.
   *
   * Returns 'sms' only if a phone input is visible and email is not.
   * Defaults to 'email' — prevents false sms_box_fired from hidden inputs.
   */
  function formType(el) {
    var override = el.getAttribute('data-klaviyo-form-type');
    if (override === 'email' || override === 'sms') return override;
    var email = el.querySelector(CONFIG.sel.email);
    var sms   = el.querySelector(CONFIG.sel.sms);
    var emailVis = isVisible(email);
    var smsVis   = isVisible(sms);
    // SMS only when phone input is showing and email is hidden (SMS step)
    if (smsVis && !emailVis) return 'sms';
    return 'email';
  }

  function params(el) {
    return { form_type: formType(el), form_id: formId(el), form_name: formName(el), placement: formPlacement(el) };
  }

  // === BINDING ==============================================================
  var _states = new WeakMap();

  /**
   * Global set of already-fired events, keyed by 'form_id::form_type'.
   * Persists across DOM element re-creation (Klaviyo replaces form node on
   * success state), so _box_fired is guaranteed to fire at most once per
   * form_id + form_type combination for the lifetime of the page.
   */
  var _firedForms = {};

  function state(el) {
    if (!_states.has(el)) _states.set(el, {});
    return _states.get(el);
  }

  function markBound(el) {
    if (el.__klBound) return true;
    el.__klBound = true;
    return false;
  }

  /**
   * Check whether a clicked/pressed element is a real submit button and not
   * a utility button such as a country-code dropdown opener.
   *
   * WHY needed (v2.12.0):
   *   Klaviyo's phone field embeds a country-code selector (flag + dial code) as a
   *   <button class="..kl-private.."> inside the same .klaviyo-form container.
   *   Without this guard our capture listeners would call onSubmit() when the user
   *   merely clicks the country-code picker, firing sms_box_submitted prematurely.
   *
   *   First-line defence: the kl-private selector already excludes [aria-haspopup]
   *   and [aria-expanded] (standard ARIA attributes on dropdown triggers).
   *   This function adds the same check for the capture listeners to stay in sync.
   */
  function isRealSubmitTarget(t) {
    if (!t) return false;
    var el = (t.matches && t.matches(CONFIG.sel.submit)) ? t
           : (t.closest && t.closest(CONFIG.sel.submit));
    if (!el) return false;
    // Dropdown-opener buttons: aria-haspopup or aria-expanded present
    if (el.getAttribute('aria-haspopup') !== null) return false;
    if (el.getAttribute('aria-expanded') !== null) return false;
    if (el.getAttribute('role') === 'combobox') return false;
    return true;
  }

  function bindInputs(formEl, selector, handler) {
    formEl.querySelectorAll(selector).forEach(function (inp) {
      if (markBound(inp)) return;
      inp.addEventListener('focus', handler, { once: true, passive: true });
      inp.addEventListener('input', handler, { once: true, passive: true });
    });
  }

  function bindSubmits(formEl, handler) {
    formEl.querySelectorAll('form').forEach(function (f) {
      if (markBound(f)) return;
      f.addEventListener('submit', handler);
    });
    formEl.querySelectorAll(CONFIG.sel.submit).forEach(function (btn) {
      if (markBound(btn)) return;
      btn.addEventListener('click', handler);
    });
  }

  function bindForm(formEl) {
    var s = state(formEl);
    var p = params(formEl);

    /**
     * Tracks the active form step (email | sms) for _box_fired transition detection.
     * Updated by the inner MutationObserver when Klaviyo changes class/style on
     * step wrappers (indicating a step transition).
     */
    var _lastFiredType = p.form_type;

    /**
     * Snapshot of formType() taken at pointerdown / keydown — i.e. BEFORE any
     * click or submit handler (including Klaviyo's own) can mutate the DOM.
     *
     * WHY needed (v2.10.0):
     *   With attributes:true on the inner MO, Klaviyo's class changes during
     *   email validation can prematurely update _lastFiredType to 'sms' before
     *   the user actually submits.  onSubmit then reads 'sms' instead of 'email'
     *   → email_box_submitted is lost, sms_box_submitted fires too early.
     *
     *   pointerdown fires BEFORE the click event and BEFORE any Klaviyo handler,
     *   so _pendingType always reflects the step the user is actually submitting.
     *   For Enter-key submits, keydown fires before the form submit event.
     */
    var _pendingType = null;
    function _captureType() { _pendingType = formType(formEl); }
    formEl.addEventListener('pointerdown', function (e) {
      // isRealSubmitTarget() excludes dropdown-opener buttons (country-code selectors)
      if (isRealSubmitTarget(e.target)) _captureType();
    }, { capture: true, passive: true });
    formEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.keyCode === 13) _captureType();
    }, { capture: true, passive: true });

    /**
     * Fire _box_fired once, but only when the form is actually visible in the
     * viewport.  Footer/embed forms exist in the DOM long before the user scrolls
     * to them — firing immediately would produce phantom events.
     *
     * Strategy:
     *  1. If form is already in viewport → fire now.
     *  2. If off-screen and IntersectionObserver is available → defer until visible.
     *  3. Fallback (no IO support) → fire immediately (old behaviour).
     *
     * Special case: zero-BCR wrappers (e.g. <back-in-stock>) were already gated
     * by the .active class check in scan(), so they're always considered "in view".
     */
    function fireFired() {
      if (s.fired) return;
      var cp = params(formEl);
      var fkey = cp.form_id + '::' + cp.form_type;
      if (_firedForms[fkey]) { s.fired = true; return; } // already fired on a previous element
      s.fired = true;
      _firedForms[fkey] = true;
      send(cp.form_type + '_box_fired', cp);
    }

    if (!s.fired) {
      var r = formEl.getBoundingClientRect();
      var zeroBCR = r.width === 0 && r.height === 0;
      if (zeroBCR) {
        // BIS-style wrapper (height:0 always): .active gate already passed in scan().
        fireFired();
      } else if (typeof IntersectionObserver !== 'undefined') {
        // Always use IntersectionObserver for dimension-having forms.
        //
        // WHY not a simple BCR check (v2.13.0):
        //   bindForm() may be called mid-render (when Klaviyo removes its
        //   visibility:hidden FOUC guard via a DOM mutation).  At that moment
        //   the form's BCR can briefly appear within the viewport before the
        //   browser reflows it to its final footer position, causing a false
        //   "in view" and firing email_box_fired immediately.
        //
        //   IntersectionObserver calls back:
        //   • For already-visible popup/embed forms: on the next animation frame
        //     (~16 ms) — imperceptible delay, preserves correct event order.
        //   • For off-screen footer forms: when the user actually scrolls to them.
        new IntersectionObserver(function (entries, obs) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) { obs.disconnect(); fireFired(); }
          });
        }, { threshold: 0.1 }).observe(formEl);
      } else {
        fireFired(); // IE / no-IO fallback
      }
    }

    function onStart() {
      var cp = params(formEl);
      var k = cp.form_type + 'Started';
      if (s[k]) return;
      s[k] = true;
      send(cp.form_type + '_box_started', cp);
    }

    function onSubmit() {
      // Use _pendingType (captured at pointerdown/keydown, before any DOM
      // transition) as the authoritative step type.  Fall back to formType()
      // for programmatic / non-pointer submits.
      var type = _pendingType !== null ? _pendingType : formType(formEl);
      // Do NOT clear _pendingType synchronously.  A submit button click fires
      // BOTH a 'click' event AND a 'submit' event on the <form>, both within
      // the same synchronous task.  Clearing immediately on the first call
      // leaves _pendingType=null for the second, which falls back to formType()
      // (already transitioned) and fires the wrong sms_box_submitted.
      // setTimeout(0) clears after the entire synchronous event cycle finishes.
      var _snap = _pendingType;
      setTimeout(function() { if (_pendingType === _snap) _pendingType = null; }, 0);
      // Safety net for country-code dropdown buttons that may lack aria-haspopup:
      // only fire sms_box_submitted when the phone input actually has content.
      // Country-code selection happens before the user types a number, so at that
      // moment the tel input is empty (or contains only the dial prefix with < 4 digits).
      if (type === 'sms') {
        var telEl = formEl.querySelector(CONFIG.sel.sms);
        if (telEl && isVisible(telEl) && telEl.value.replace(/[^0-9]/g, '').length < 4) return;
      }
      var cp   = params(formEl);
      cp.form_type = type;
      var k = type + 'Submitted';
      if (s[k]) return;
      s[k] = true;
      send(type + '_box_submitted', cp);
    }

    bindInputs(formEl, CONFIG.sel.email, onStart);
    bindInputs(formEl, CONFIG.sel.sms, onStart);
    bindSubmits(formEl, onSubmit);

    // Capture-phase click fallback: custom elements (e.g. klaviyo-subscription-form)
    // may call stopImmediatePropagation() on button click handlers registered before
    // the bridge, preventing our target-phase handler from firing.  Capture phase on
    // formEl fires before the target phase on the button — immune to stopImmediate.
    formEl.addEventListener('click', function (e) {
      // isRealSubmitTarget() excludes dropdown-opener buttons (country-code selectors)
      if (isRealSubmitTarget(e.target)) onSubmit();
    }, { capture: true });

    // Inner MutationObserver: re-bind newly added inputs/buttons (Klaviyo injects
    // them dynamically), and detect multi-step transitions (email → sms step).
    //
    // IMPORTANT: only fire _box_fired when the FORM TYPE changes (e.g. email step
    // done, SMS step appears).  Do NOT fire on every DOM mutation — Klaviyo adds
    // validation nodes on each keystroke, which would spam duplicate _box_fired
    // events (partially masked by 2-s dedup but visible when user pauses typing).
    new MutationObserver(function () {
      bindInputs(formEl, CONFIG.sel.email, onStart);
      bindInputs(formEl, CONFIG.sel.sms, onStart);
      bindSubmits(formEl, onSubmit);
      var cp = params(formEl);
      if (cp.form_type !== _lastFiredType) {
        _lastFiredType = cp.form_type;
        var fkey = cp.form_id + '::' + cp.form_type;
        if (!_firedForms[fkey]) {
          _firedForms[fkey] = true;
          send(cp.form_type + '_box_fired', cp);
        }
      }
    }).observe(formEl, {
      childList:       true,
      subtree:         true,
      // Also watch attribute changes: Klaviyo toggles steps by adding/removing
      // CSS classes or inline styles on wrappers (not by inserting/removing nodes).
      // Without this, the step transition (email → sms) is invisible to the MO,
      // _lastFiredType is never updated, and sms_box_fired / sms_box_submitted
      // are never sent.
      attributes:      true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden']
    });

    log('bound', p.form_id);
  }

  // === DISCOVERY ============================================================
  var _seen = new WeakSet();

  function scan() {
    document.querySelectorAll(CONFIG.sel.form).forEach(function (el) {
      if (_seen.has(el)) return;

      // ── Anti-nesting: prefer the innermost matched container ──
      // If this element contains a descendant that also matches sel.form,
      // skip it — the descendant will be bound instead.  This prevents
      // double-fire when e.g. div[role="dialog"] wraps a .klaviyo-form.
      if (el.querySelector(CONFIG.sel.form)) {
        _seen.add(el);  // mark so we don't re-check
        return;
      }

      // ── Visibility gate ──
      var r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) {
        // Zero-BCR can happen for popup wrappers that have height:0 and
        // position their content via child fixed/absolute elements (e.g. the
        // theme <back-in-stock> custom element).  In these cases the only
        // reliable signal that the popup is open is an inner element with an
        // activation class.  We accept aria-expanded="true" (ARIA standard)
        // or the common .active class pattern used by Shopify themes.
        if (!el.querySelector('[aria-expanded="true"], .active')) return;
      }
      // Use isVisible() — more reliable than getComputedStyle().visibility.
      // Klaviyo sets visibility:hidden on <klaviyo-subscription-form> as a FOUC
      // guard while keeping its children visually rendered (children override to
      // visibility:visible).  A raw visibility === 'hidden' check wrongly skips
      // this form entirely.  isVisible() uses offsetParent / offsetWidth which
      // reflect actual render state regardless of CSS visibility tricks.
      if (!isVisible(el)) return;

      _seen.add(el);
      bindForm(el);
    });
  }

  function init() {
    log('init v2.13.0-light | iframeSel:', CONFIG.iframeSel);
    scan();
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i];
        if (m.addedNodes.length || m.type === 'attributes') {
          setTimeout(scan, 150);
          return;
        }
      }
    }).observe(document.body, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['style', 'class']
    });
  }

  if (document.body) init();
  else document.addEventListener('DOMContentLoaded', init);
})();
